import {
  ReunionTipo,
  ReunionEstado,
  ReunionCanal,
  ReunionTipoAsistentes,
  ReunionOrigen,
  ReunionTipoEventoReferencia,
  ReglaRolStaffDestino,
  ReunionParticipanteRol,
  ReunionParticipanteEstado,
} from '../types/reuniones';
import i18n from '../i18n';

// Enum arrays for dropdowns
export const REUNION_TIPO_VALUES: ReunionTipo[] = [
  'bienvenida',
  'seguimiento',
  'incidencia',
  'otro',
];
export const REUNION_ESTADO_VALUES: ReunionEstado[] = [
  'programada',
  'realizada',
  'no_show_familia',
  'no_show_staff',
  'cancelada_familia',
  'cancelada_staff',
  'cancelada_sistema',
];
export const REUNION_CANAL_VALUES: ReunionCanal[] = ['telefonico', 'online', 'presencial'];
export const REUNION_TIPO_ASISTENTES_VALUES: ReunionTipoAsistentes[] = [
  'solo_tutores',
  'solo_alumno',
  'tutores_y_alumno',
];
export const REUNION_ORIGEN_VALUES: ReunionOrigen[] = ['manual', 'pasarela_ventas', 'otro'];
export const REUNION_TIPO_EVENTO_REFERENCIA_VALUES: ReunionTipoEventoReferencia[] = [
  'nueva_matricula',
  'incidencia',
  'otro',
];
export const REGLA_ROL_STAFF_DESTINO_VALUES: ReglaRolStaffDestino[] = [
  'att_familias',
  'att_alumnado',
  'admisiones',
  'otro',
];

// Format meeting duration
export function formatDuracion(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;

  if (horas === 0) {
    return `${mins} min`;
  } else if (mins === 0) {
    return `${horas} h`;
  } else {
    return `${horas} h ${mins} min`;
  }
}

// Format date for display
export function formatFecha(date: string | Date, includeTime: boolean = true): string {
  const d = new Date(date);

  if (includeTime) {
    return d.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } else {
    return d.toLocaleDateString('es-ES');
  }
}

// Get display label for enum values
export function getTipoDisplayLabel(tipo: ReunionTipo): string {
  const labels: Record<ReunionTipo, string> = {
    bienvenida: 'Bienvenida',
    seguimiento: 'Seguimiento',
    incidencia: 'Incidencia',
    otro: 'Otro',
  };
  return labels[tipo] || tipo;
}

export function getEstadoDisplayLabel(estado: ReunionEstado): string {
  const labels: Record<ReunionEstado, string> = {
    programada: 'Programada',
    realizada: 'Realizada',
    no_show_familia: 'No Show Familia',
    no_show_staff: 'No Show Staff',
    cancelada_familia: 'Cancelada por Familia',
    cancelada_staff: 'Cancelada por Staff',
    cancelada_sistema: 'Cancelada por Sistema',
  };
  return labels[estado] || estado.replace(/_/g, ' ');
}

// Calculate meeting duration in minutes
export function calcularDuracion(fechaInicio: Date, fechaFin: Date): number {
  const diffMs = fechaFin.getTime() - fechaInicio.getTime();
  return Math.round(diffMs / (1000 * 60));
}

// Validate meeting dates
export function validarFechasReunion(fechaInicio: Date, fechaFin: Date): string | null {
  const ahora = new Date();

  if (fechaInicio < ahora) {
    return 'La fecha de inicio no puede ser en el pasado';
  }

  if (fechaFin <= fechaInicio) {
    return 'La fecha de fin debe ser posterior a la fecha de inicio';
  }

  const duracion = calcularDuracion(fechaInicio, fechaFin);
  if (duracion < 15) {
    return 'La duración mínima de la reunión es de 15 minutos';
  }

  if (duracion > 240) {
    return 'La duración máxima de la reunión es de 4 horas';
  }

  return null;
}
/* ══════════════════════════════════════════════════════════════
   CONSTANTES PARA PARTICIPANTES
 ══════════════════════════════════════════════════════════════ */

export const REUNION_PARTICIPANTE_ROL_VALUES: ReunionParticipanteRol[] = [
  'staff_principal',
  'staff_secundario',
  'alumno_principal',
  'alumno_secundario',
  'invitado',
];

export const REUNION_PARTICIPANTE_ESTADO_VALUES: ReunionParticipanteEstado[] = [
  'pendiente',
  'confirmado',
  'rechazado',
  'cancelado',
  'asistio',
  'no_asistio',
];

/* ══════════════════════════════════════════════════════════════
   FUNCIONES DE FORMATO PARA PARTICIPANTES
 ══════════════════════════════════════════════════════════════ */

export function getParticipanteRolLabel(rol: ReunionParticipanteRol): string {
  const labels: Record<ReunionParticipanteRol, string> = {
    staff_principal: 'Staff Principal',
    staff_secundario: 'Staff Secundario',
    alumno_principal: 'Alumno Principal',
    alumno_secundario: 'Alumno Secundario',
    invitado: 'Invitado',
  };
  return labels[rol] || rol;
}

export function getParticipanteEstadoLabel(estado: ReunionParticipanteEstado): string {
  const labels: Record<ReunionParticipanteEstado, string> = {
    pendiente: 'Pendiente',
    confirmado: 'Confirmado',
    rechazado: 'Rechazado',
    cancelado: 'Cancelado',
    asistio: 'Asistió',
    no_asistio: 'No Asistió',
  };
  return labels[estado] || estado;
}

// Get display label for tipoAsistentes using i18n
export function getTipoAsistentesLabel(tipo: ReunionTipoAsistentes): string {
  return i18n.t(`reuniones.attendees.${tipo}`);
}

/* ══════════════════════════════════════════════════════════════
   FUNCIONES PARA REGLAS DE AGENDACIÓN
 ══════════════════════════════════════════════════════════════ */

export function getRolStaffDestinoLabel(rol: ReglaRolStaffDestino): string {
  const labels: Record<ReglaRolStaffDestino, string> = {
    att_familias: 'Atención a Familias',
    att_alumnado: 'Atención al Alumnado',
    admisiones: 'Admisiones',
    otro: 'Otro',
  };
  return labels[rol] || rol;
}

export function formatDiasRegla(min: number | null, max: number | null): string {
  if (min === null && max === null) return 'Sin límite';
  if (min === null) return `Hasta ${max} días`;
  if (max === null) return `Desde ${min} días`;
  return `${min}–${max} días`;
}
